/** Shared types, enums and small data shapes used across the game. */

export enum Direction {
  None = 'none',
  Up = 'up',
  Down = 'down',
  Left = 'left',
  Right = 'right',
}

/** Grid deltas for each direction. Row increases downward (−Z), col increases rightward (+X). */
export const DIR_DELTA: Record<Direction, { dr: number; dc: number }> = {
  [Direction.None]: { dr: 0, dc: 0 },
  [Direction.Up]: { dr: -1, dc: 0 },
  [Direction.Down]: { dr: 1, dc: 0 },
  [Direction.Left]: { dr: 0, dc: -1 },
  [Direction.Right]: { dr: 0, dc: 1 },
}

export const OPPOSITE: Record<Direction, Direction> = {
  [Direction.None]: Direction.None,
  [Direction.Up]: Direction.Down,
  [Direction.Down]: Direction.Up,
  [Direction.Left]: Direction.Right,
  [Direction.Right]: Direction.Left,
}

export const ALL_DIRS: Direction[] = [Direction.Up, Direction.Down, Direction.Left, Direction.Right]

export interface GridPos {
  row: number
  col: number
}

/** Top-level screens. HUD / pause / gameover overlays render on top of 'play'. */
export type ScreenName = 'title' | 'menu' | 'play' | 'shop' | 'leaderboard' | 'settings'

/** In-match lifecycle. */
export type MatchStatus = 'ready' | 'playing' | 'paused' | 'dying' | 'levelclear' | 'gameover'

/** Jellyfish behaviour archetypes (Pac-Man ghost personalities). */
export type JellyKind = 'chaser' | 'ambusher' | 'patroller' | 'wanderer'

/** Global AI mode shared by all jellyfish. */
export type AIMode = 'scatter' | 'chase' | 'frightened' | 'eaten'

export interface LeaderboardEntry {
  nickname: string
  score: number
  level: number
  jelly_earned: number
}

export interface ShopItemData {
  id: string
  name: string
  type: string
  cost: number
  description: string
}

export type Quality = 'low' | 'medium' | 'high'

export interface Settings {
  master: number
  music: number
  sfx: number
  quality: Quality
  reduceMotion: boolean
  /** Target render height in physical px; 0 = native panel resolution (default). */
  resolution: number
}

/** Live snapshot the HUD and test harness read each frame. */
export interface HudState {
  score: number
  hiScore: number
  lives: number
  level: number
  jelly: number
  pearlsRemaining: number
  powered: boolean
  powerRatio: number // 0..1 remaining
}
